import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import SecondaryButton from "../buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import CheckBox from "../selection/CheckBox";
import CheckBoxInput from "../selection/CheckBoxInput";
import { ChunkyTabs } from "../selection/ChunkyTabs";
import SearchAlgolia from "../search/SearchAlgolia";

// Add column configuration button
type ColumnType = {
  field: string;
  headerName: string;
  width?: number;
  flex?: number;
  type?: string;
  sortable?: boolean;
  renderCell?: (params: {
    value: any;
    row: any;
    field: string;
    colDef: ColumnType;
  }) => React.ReactNode;
};

interface DataGridProps {
  table: { columns: ColumnType[]; rows: any[] };
  show_ranking?: boolean;
  onClick?: (row: any) => void;
  is_folders?: boolean;
  initial_sort_field?: string;
  initial_asc?: string;
  handleSortChange?: (sortModel: { field: string; sort: string }[]) => void;
  handleSelectionModelChange?: (selectionModel: any[]) => void;
  initial_saved_columns_sizes?: { [key: string]: number };
  initial_saved_columns_showed?: { [key: string]: boolean };
  initialPage?: number;
  initialPageSize?: number;
  setPaginationModel?: (model: any) => void;
  handleColumnManagementChange?: (model: any) => void;
  handleColumnSizeChange?: (params: {
    colDef: { width: number; field: string };
  }) => void;
  selectable?: boolean;
  orgData?: any;
  handleResetConfig?: () => void;
  hide_lines?: boolean;
  tabs?: { value: string; label: string }[];
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  onSearch?: (query: string) => Promise<any[]>;
  onSearchSelect?: (option: any) => void;
  onNext?: () => void;
  onPrev?: () => void;
  hasNext?: boolean;
  hasPrev?: boolean;
  totalRows?: number;
  serverPage?: number;
  totalServerPages?: number;
  onServerPageChange?: (page: number) => void;
  extraSearchContent?: React.ReactNode;
  storageKey?: string;
  serverSideSorted?: boolean;
  /** When provided, renders a floating panel (portaled to body, positioned at the
   *  cursor) while hovering a row — used to preview an ordre's interventions. */
  renderRowHover?: (row: any) => React.ReactNode;
}

const DataGrid = ({
  table,
  show_ranking = false,
  onClick = () => {},
  is_folders = false,
  initial_sort_field = "id",
  initial_asc = "asc",
  handleSortChange = () => {},
  handleSelectionModelChange = () => {},
  initial_saved_columns_sizes = {},
  hide_lines = false,
  initial_saved_columns_showed = is_folders
    ? {
        id: false,
        num: false,
        state: false,
      }
    : { id: false }, // Set the default initial column visibility
  initialPage = 0,
  initialPageSize = 50,
  setPaginationModel = () => {},
  handleColumnManagementChange = () => {},
  handleColumnSizeChange = () => {},
  selectable = true,
  orgData,
  handleResetConfig = () => {},
  tabs,
  activeTab: activeTabProp,
  onTabChange,
  onSearch,
  onSearchSelect,
  onNext,
  onPrev,
  hasNext = false,
  hasPrev = false,
  totalRows,
  serverPage,
  totalServerPages,
  onServerPageChange,
  extraSearchContent,
  storageKey,
  serverSideSorted = false,
  renderRowHover,
}: DataGridProps) => {
  // Pagination state

  const { columns, rows } = table;
  // Floating hover-preview panel (portaled to body, follows the cursor)
  const [rowHover, setRowHover] = useState<{ row: any; x: number; y: number } | null>(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [rowSelectionModel, onRowSelectionModelChange] = useState<any>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  // Column configuration dropdown state
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const columnConfigRef = useRef(null);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: initial_sort_field,
    direction: initial_asc,
  });

  // Load persisted column config if storageKey is provided
  const loadedConfig = React.useMemo(() => {
    if (!storageKey) return null;
    try {
      const raw = localStorage.getItem(`datagrid-${storageKey}`);
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  }, [storageKey]);

  // Column visibility state
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    loadedConfig?.visibility ??
    (is_folders
      ? {
          ...initial_saved_columns_showed,
          state: searchValue === "" ? false : true,
        }
      : initial_saved_columns_showed),
  );

  // Column sizes state
  const [columnSizes, setColumnSizes] = useState(
    loadedConfig?.sizes ?? initial_saved_columns_sizes,
  );

  // Initialize flag
  const [initialised, setInitialised] = useState(false);

  // References to prevent infinite loops
  const isFirstRender = useRef(true);
  const prevSearchValue = useRef(searchValue);

  const scrollRef = useRef<any>(null);
  const dragScrollRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ isDragging: false, startX: 0, scrollLeft: 0 });

  const dragDidMove = useRef(false);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    const el = dragScrollRef.current;
    if (!el) return;
    const tag = (e.target as HTMLElement).closest("input, button, a, select, [role='button']");
    if (tag) return;
    dragState.current = { isDragging: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft };
    dragDidMove.current = false;
    el.style.cursor = "grabbing";
    el.style.userSelect = "none";
  }, []);

  const onDragEnd = useCallback(() => {
    const el = dragScrollRef.current;
    if (!el) return;
    dragState.current.isDragging = false;
    el.style.cursor = "grab";
    el.style.userSelect = "";
  }, []);

  const onDragMove = useCallback((e: React.MouseEvent) => {
    if (!dragState.current.isDragging) return;
    const el = dragScrollRef.current;
    if (!el) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = x - dragState.current.startX;
    if (Math.abs(walk) > 3) dragDidMove.current = true;
    el.scrollLeft = dragState.current.scrollLeft - walk;
  }, []);

  const resetScroll = () => {
    if (dragScrollRef.current) {
      dragScrollRef.current.scrollTop = 0;
    }
  };

  // Selection state

  // Handle click outside column config dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        columnConfigRef.current &&
        !(columnConfigRef.current as HTMLElement | null)?.contains(event.target)
      ) {
        setColumnConfigOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Initialize component
  useEffect(() => {
    setInitialised(true);
  }, []);

  // Effect to update pagination model when page or size changes

  // Effect to set initial column visibility
  useEffect(() => {
    if (prevSearchValue.current !== searchValue) {
      prevSearchValue.current = searchValue;

      const new_column_visibility_model = { ...columnVisibilityModel };

      if (is_folders) {
        if (searchValue !== "") {
          new_column_visibility_model["state"] = true;
        } else {
          new_column_visibility_model["state"] = false;
        }
      }

      setColumnVisibilityModel(new_column_visibility_model);

      // Use a timeout to prevent circular updates
      const timeoutId = setTimeout(() => {
        handleColumnManagementChange(new_column_visibility_model);
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [
    searchValue,
    is_folders,
    columnVisibilityModel,
    handleColumnManagementChange,
  ]);

  // Pagination logic
  const totalPages = Math.ceil(rows.length / pageSize);

  function lowerAndTrim(str: any, key: any = null) {
    if (key === "all_tags") {
      let tags = str?.filter((item: any) => item?.org_ref.id === orgData?.id);
      let tag_str = tags?.map((item: any) => item?.nom);
      //return str?.map((item) => item.toLowerCase().trim());
      return tag_str;
    }
    if (typeof str !== "string") {
      return str;
    }
    return str?.toLowerCase()?.trim();
  }

  // Sorting logic — skip when server already provides sorted data
  const sortedRows = React.useMemo(() => {
    if (serverSideSorted) return rows;
    const sortableRows = [...rows];
    if (sortConfig.key !== null) {
      sortableRows.sort((a, b) => {
        if (
          lowerAndTrim(a[sortConfig.key], sortConfig.key) <
          lowerAndTrim(b[sortConfig.key], sortConfig.key)
        ) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (
          lowerAndTrim(a[sortConfig.key], sortConfig.key) >
          lowerAndTrim(b[sortConfig.key], sortConfig.key)
        ) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableRows;
  }, [rows, sortConfig, serverSideSorted]);
  const paginatedRows = sortedRows.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize,
  );

  // Handler for sort request
  const requestSort = (key: any) => {
    // Check if column is sortable
    const column = columns.find((c: any) => c.field === key);
    if (column && column.sortable === false) return;

    if (!initialised) {
      setInitialised(true);
    }

    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });

    // Call external sort handler
    handleSortChange(key && direction ? [{ field: key, sort: direction }] : []);
  };

  // Persist column config to localStorage
  const persistColumnConfig = React.useCallback(
    (visibility: Record<string, boolean>, sizes: Record<string, number>) => {
      if (!storageKey) return;
      try {
        localStorage.setItem(
          `datagrid-${storageKey}`,
          JSON.stringify({ visibility, sizes }),
        );
      } catch {}
    },
    [storageKey],
  );

  // Toggle a single column's visibility
  const toggleColumnVisibility = (field: any) => {
    const new_model = { ...columnVisibilityModel };
    new_model[field] = !new_model[field];

    setColumnVisibilityModel(new_model);
    persistColumnConfig(new_model, columnSizes);

    // Use a timeout to prevent circular updates
    setTimeout(() => {
      handleColumnManagementChange(new_model);
    }, 0);
  };

  // Handler for column resize
  const handleColumnResize = (field: any, newWidth: any) => {
    const newSizes = {
      ...columnSizes,
      [field]: newWidth,
    };
    setColumnSizes(newSizes);
    persistColumnConfig(columnVisibilityModel as Record<string, boolean>, newSizes);

    // Use a timeout to prevent potential circular updates
    handleColumnSizeChange({
      colDef: {
        width: newWidth,
        field: field,
      },
    });
  };

  // Row selection handler
  const handleRowSelectionChange = (id: any) => {
    let newSelection;

    if (rowSelectionModel.includes(id)) {
      // If row is already selected, remove it
      newSelection = rowSelectionModel.filter((rowId: any) => rowId !== id);
    } else {
      // If row is not selected, add it to selection
      newSelection = [...rowSelectionModel, id];
    }

    onRowSelectionModelChange(newSelection);
    handleSelectionModelChange(newSelection);
  };

  // Handler for row click — suppress if user was dragging
  const handleRowClick = (row: any) => {
    if (dragDidMove.current) return;
    onClick(row);
  };

  // Function to calculate column width based on width or flex
  const getColumnWidth = (column: any) => {
    // Use custom saved width if available
    if (columnSizes[column.field] !== undefined) {
      return `${columnSizes[column.field]}px`;
    }
    // Use explicit width if provided
    if (column.width) {
      return `${column.width}px`;
    }

    // Use flex proportional width if provided
    if (column.flex) {
      // For flex, we'll convert it to a percentage of the table width
      // This is a simplified approach - actual flex would need container refs

      if ((column.flex * 100) / totalFlexUnits < (column.minWidth || 100)) {
        return `${column.minWidth || 100}px`;
      } else {
        return `${(column.flex * 100) / totalFlexUnits}%`;
      }
    }

    // Default width if nothing specified
    return "auto";
  };

  // Calculate total flex units once
  const totalFlexUnits = React.useMemo(() => {
    return columns.reduce((total, column) => total + (column.flex || 0), 0);
  }, [columns]);

  // Helper function to render cell content
  const renderCellContent = (column: any, row: any) => {
    // If the column has a renderCell function, use it
    if (column.renderCell) {
      // Create params object similar to MUI DataGrid
      const params = {
        value: row[column.field],
        row: row,
        field: column.field,
        colDef: column,
      };
      return column.renderCell(params);
    }

    const value = row[column.field];

    // Handle date type columns
    if (column.type === "date" && value instanceof Date) {
      return value.toLocaleDateString();
    }

    // Handle date strings that need to be converted
    if (column.type === "date" && typeof value === "string") {
      try {
        const date = new Date(value);
        return date.toLocaleDateString();
      } catch (e) {
        return value; // Return original value if date conversion fails
      }
    }

    // Don't render objects
    if (
      typeof value === "object" &&
      value !== null &&
      !(value instanceof Date)
    ) {
      return "[Object]";
    }

    // Otherwise, just return the cell value
    return value;
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [rows]);

  // Get visible columns
  const visibleColumns = React.useMemo(() => {
    return columns.filter((col) => columnVisibilityModel[col.field] !== false);
  }, [columns, columnVisibilityModel]);

  return (
    <div className="w-full h-full border max-w-full overflow-hidden border-neutral-200 rounded-xl shadow flex flex-col">
      <div className="flex-shrink-0 bg-neutral-50 rounded-t-xl overflow-x-hidden overflow-y-hidden">
        {tabs && activeTabProp && onTabChange && (
          <div className="px-2 pt-2">
            <ChunkyTabs
              tabs={tabs}
              activeTab={activeTabProp}
              setActiveTab={(tab) => onTabChange(tab)}
            />
          </div>
        )}
        <div className="px-2 py-2 flex flex-row items-center gap-3">
          <div className="flex-1 min-w-[200px] max-w-[400px]">
            <SearchAlgolia
              value={searchValue}
              setValue={(val) => setSearchValue(val)}
              placeholder="Rechercher..."
              show_options={true}
              onSearch={onSearch}
              onAction={(option) => {
                if (
                  typeof option === "object" &&
                  option.value &&
                  onSearchSelect
                ) {
                  onSearchSelect(option);
                  return true;
                }
              }}
            />
          </div>
          {extraSearchContent}
        </div>
      </div>
      <div
        ref={dragScrollRef}
        className="flex-1 min-h-0 overflow-x-auto overflow-y-auto scrollbar-hide"
        style={{ cursor: "grab" }}
        onMouseDown={onDragStart}
        onMouseUp={onDragEnd}
        onMouseLeave={onDragEnd}
        onMouseMove={onDragMove}
      >
        <table
          ref={scrollRef}
          className="border-collapse w-full"
          style={{ tableLayout: "fixed", borderSpacing: 0, minWidth: "max-content" }}
        >
          <colgroup>
            {selectable && (
              <col style={{ width: "55px" }} />
            )}
            {show_ranking && (
              <col style={{ width: "40px" }} />
            )}
            {visibleColumns.map((column) => (
              <col
                key={`col-${column.field}`}
                style={{ width: getColumnWidth(column) }}
              />
            ))}
          </colgroup>
          <thead className="sticky top-0 z-20 bg-neutral-50">
            <tr>
              {selectable && (
                <th
                  style={{
                    paddingLeft: 15,
                    overflow: "hidden",
                    boxSizing: "border-box",
                    left: 0,
                    zIndex: 10,
                  }}
                >
                  <CheckBox
                    checked={
                      rows.length > 0 &&
                      rowSelectionModel.length === rows.length
                    }
                    onChange={(checked) => {
                      if (checked) {
                        const allIds = rows.map((row) => row.id);
                        onRowSelectionModelChange(allIds);
                        handleSelectionModelChange(allIds);
                      } else {
                        onRowSelectionModelChange([]);
                        handleSelectionModelChange([]);
                      }
                    }}
                  ></CheckBox>
                </th>
              )}
              {show_ranking && (
                <th style={{ width: "40px", maxWidth: "40px", minWidth: "40px" }} />
              )}
              {visibleColumns.map((column) => (
                <th
                  key={column.field}
                  className={`
                      ${
                        sortConfig.key === column.field
                          ? "border-b-2 border-neutral-500"
                          : ""
                      }
                      text-left pr-4 text-neutral-500 p-2 font-medium text-h3 uppercase w-fit relative ${column.sortable !== false ? "cursor-pointer" : ""} whitespace-nowrap select-none`}
                  onClick={() => requestSort(column.field)}
                >
                  <div className="flex items-center space-x-1">
                    <span className="text-ellipsis text-h5 text-neutral-400 overflow-hidden whitespace-nowrap">
                      {column.headerName}
                    </span>
                    {sortConfig.key === column.field && (
                      <span className="pl-0 rounded-full">
                        {sortConfig.direction === "asc" ? (
                          <Icon1 name="up2" size={15} color="black"></Icon1>
                        ) : (
                          <Icon1 name="down2" size={15} color="black"></Icon1>
                        )}
                      </span>
                    )}
                  </div>
                  <div
                    className="absolute right-0 top-0 h-full w-1 rounded-lg bg-neutral-100 cursor-col-resize hover:bg-neutral-300"
                    onMouseDown={(e) => {
                      const startX = e.pageX;
                      const startWidth =
                        columnSizes[column.field] || column.width || 100;

                      const onMouseMove = (moveEvent: any) => {
                        const newWidth = Math.max(
                          80,
                          startWidth + (moveEvent.pageX - startX),
                        );
                        handleColumnResize(column.field, newWidth);
                      };

                      const onMouseUp = () => {
                        document.removeEventListener(
                          "mousemove",
                          onMouseMove,
                        );
                        document.removeEventListener("mouseup", onMouseUp);
                      };

                      document.addEventListener("mousemove", onMouseMove);
                      document.addEventListener("mouseup", onMouseUp);
                    }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody
            className={`
              ${hide_lines ? "" : "divide-y divide-neutral-200"}
              bg-white `}
          >
            {paginatedRows.map((row, rowIndex) => (
              <tr
                key={row.id || rowIndex}
                className={`hover:bg-neutral-50 cursor-pointer ${
                  rowSelectionModel.includes(row.id) ? "bg-neutral-50" : ""
                }`}
                onClick={() => handleRowClick(row)}
                onMouseMove={
                  renderRowHover
                    ? (e) => setRowHover({ row, x: e.clientX, y: e.clientY })
                    : undefined
                }
                onMouseLeave={renderRowHover ? () => setRowHover(null) : undefined}
              >
                {selectable && (
                  <td
                    style={{
                      paddingLeft: 15,
                      overflow: "hidden",
                      boxSizing: "border-box",
                      zIndex: -1,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <CheckBox
                      checked={rowSelectionModel.includes(row.id)}
                      onChange={() => {
                        handleRowSelectionChange(row.id);
                      }}
                    ></CheckBox>
                  </td>
                )}
                {show_ranking && (
                  <td
                    style={{
                      width: "40px",
                      minWidth: "40px",
                      maxWidth: "40px",
                    }}
                    className="text-h3 text-shadow_50"
                  >
                    {rowIndex + 1}
                  </td>
                )}
                {visibleColumns.map((column) => (
                  <td
                    key={`${row.id || rowIndex}-${column.field}`}
                    style={{
                      padding: "8px 12px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      boxSizing: "border-box",
                    }}
                    className="text-h2 text-neutral-500"
                  >
                    {renderCellContent(column, row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {renderRowHover && rowHover && createPortal(
        <div
          style={{
            position: "fixed",
            left: Math.min(rowHover.x + 16, window.innerWidth - 340),
            top: Math.min(rowHover.y + 16, window.innerHeight - 40),
            zIndex: 10001,
            pointerEvents: "none",
          }}
        >
          {renderRowHover(rowHover.row)}
        </div>,
        document.body,
      )}

      <div className="bg-white px-4 py-2 flex items-center rounded-xl h-fit justify-between  sm:px-2 mt-auto">
        <div className="flex-1 flex h-fit justify-between sm:hidden">
          <button
            onClick={() => {
              if (currentPage === 0 && hasPrev && onPrev) { onPrev(); setCurrentPage(0); }
              else setCurrentPage(Math.max(0, currentPage - 1));
            }}
            disabled={currentPage === 0 && !hasPrev}
            className="relative inline-flex items-center px-4 py-1 border border-neutral-300 text-sm font-medium rounded-md text-neutral-550 bg-white hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Précédent
          </button>
          <button
            onClick={() => {
              if (currentPage === totalPages - 1 && hasNext && onNext) { onNext(); setCurrentPage(0); }
              else { setCurrentPage(Math.min(totalPages - 1, currentPage + 1)); }
            }}
            disabled={currentPage === totalPages - 1 && !hasNext}
            className="ml-3 relative inline-flex items-center px-4 py-1 border border-neutral-300 text-sm font-medium rounded-md text-neutral-550 bg-white hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Suivant
          </button>
        </div>

        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div className="flex flex-row items-center gap-3 w-full">
            <div className="flex items-center flex-row border  px-2 py-1 rounded-md  gap-1 text-h2">
              {/* Page size selector */}
              Affichés
              <select
                value={pageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value);
                  setPageSize(newSize);
                  setCurrentPage(0); // Reset to first page on page size change
                  resetScroll();
                }}
                className=" bg-neutral-100 font-medium mx-1 hover:ring-2 hover:ring-neutral-300  cursor-pointer rounded-md text-sm py-0  px-1"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
              </select>
              sur
              <span className="font-medium"> {totalRows ?? rows.length}</span>
            </div>{" "}
            {/* Column config button */}
            <div className="relative" ref={columnConfigRef}>
              <button
                onClick={() => setColumnConfigOpen((prev) => !prev)}
                className="inline-flex items-center gap-1.5 px-3 py-1 border border-neutral-300 text-sm font-medium rounded-md text-neutral-500 bg-white hover:bg-neutral-50"
              >
                <Icon1 name="settings" size={14} />
                Colonnes
              </button>
              {columnConfigOpen && (
                <div className="absolute bottom-full left-0 mb-2 w-220 bg-white border border-neutral-200 rounded-lg shadow-lg z-50">
                  <div className="px-3 py-2 border-b border-neutral-100 text-h4 font-medium text-neutral-500">
                    Colonnes visibles
                  </div>
                  <div className="max-h-300 overflow-y-auto p-2 flex flex-col gap-1">
                    {columns.map((col) => (
                      <CheckBoxInput
                        key={col.field}
                        checked={columnVisibilityModel[col.field] !== false}
                        onChange={() => toggleColumnVisibility(col.field)}
                        label={col.headerName}
                      />
                    ))}
                  </div>
                  {storageKey && (
                    <div className="border-t border-neutral-100 px-3 py-2">
                      <button
                        onClick={() => {
                          const defaultModel = is_folders
                            ? { id: false, num: false, state: false }
                            : { id: false };
                          setColumnVisibilityModel(defaultModel);
                          setColumnSizes({});
                          localStorage.removeItem(`datagrid-${storageKey}`);
                          setColumnConfigOpen(false);
                        }}
                        className="text-h4 text-neutral-400 hover:text-neutral-600"
                      >
                        Réinitialiser
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <nav
              className="relative z-0 inline-flex h-8 ml-auto rounded-md shadow-sm -space-x-px"
              aria-label="Pagination"
            >
              {(() => {
                // Server-side pagination mode
                if (totalServerPages && serverPage && onServerPageChange) {
                  const sp = serverPage;
                  const tp = totalServerPages;
                  const btnClass = (active: boolean) =>
                    `${active ? "bg-neutral-50 border-neutral-500 text-neutral-600" : "bg-white border-neutral-300 text-neutral-500 hover:bg-neutral-50"} relative inline-flex items-center px-4 py-1 border text-sm font-medium`;
                  const ellipsis = (key: string) => (
                    <span key={key} className="relative inline-flex items-center px-4 py-1 border border-neutral-300 bg-white text-sm font-medium text-neutral-550">...</span>
                  );
                  const pageBtn = (p: number) => (
                    <button key={p} onClick={() => { onServerPageChange(p); resetScroll(); }} className={btnClass(sp === p)}>{p}</button>
                  );

                  const buttons: React.ReactNode[] = [];
                  buttons.push(
                    <button key="prev" onClick={() => { onServerPageChange(sp - 1); resetScroll(); }} disabled={sp <= 1}
                      className="relative inline-flex items-center px-2 py-1 rounded-l-md border border-neutral-300 bg-white text-sm font-medium text-neutral-500 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed">
                      Précédent
                    </button>
                  );

                  // Page number buttons with ellipsis
                  if (tp <= 7) {
                    for (let i = 1; i <= tp; i++) buttons.push(pageBtn(i));
                  } else {
                    buttons.push(pageBtn(1));
                    if (sp > 3) buttons.push(ellipsis("e1"));
                    const start = Math.max(2, sp - 1);
                    const end = Math.min(tp - 1, sp + 1);
                    for (let i = start; i <= end; i++) buttons.push(pageBtn(i));
                    if (sp < tp - 2) buttons.push(ellipsis("e2"));
                    buttons.push(pageBtn(tp));
                  }

                  buttons.push(
                    <button key="next" onClick={() => { onServerPageChange(sp + 1); resetScroll(); }} disabled={sp >= tp}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-neutral-300 bg-white text-sm font-medium text-neutral-500 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed">
                      Suivant
                    </button>
                  );
                  return buttons;
                }

                // Client-side pagination mode (fallback)
                const buttons: React.ReactNode[] = [];
                buttons.push(
                  <button key="prev"
                    onClick={() => {
                      if (currentPage === 0 && hasPrev && onPrev) { onPrev(); setCurrentPage(0); resetScroll(); }
                      else { setCurrentPage(Math.max(0, currentPage - 1)); resetScroll(); }
                    }}
                    disabled={currentPage === 0 && !hasPrev}
                    className="relative inline-flex items-center px-2 py-1 rounded-l-md border border-neutral-300 bg-white text-sm font-medium text-neutral-500 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed">
                    Précédent
                  </button>
                );

                if (currentPage > 2) {
                  buttons.push(
                    <button key={0} onClick={() => { setCurrentPage(0); resetScroll(); }}
                      className="bg-white border-neutral-300 text-neutral-500 hover:bg-neutral-50 relative inline-flex items-center px-4 py-1 border text-sm font-medium">1</button>
                  );
                  if (currentPage > 3) buttons.push(<span key="e1" className="relative inline-flex items-center px-4 py-1 border border-neutral-300 bg-white text-sm font-medium text-neutral-550">...</span>);
                }
                const startPage = Math.max(0, currentPage - 1);
                const endPage = Math.min(totalPages - 1, currentPage + 1);
                for (let i = startPage; i <= endPage; i++) {
                  buttons.push(
                    <button key={i} onClick={() => { setCurrentPage(i); resetScroll(); }}
                      className={`${currentPage === i ? "bg-neutral-50 border-neutral-500 text-neutral-600" : "bg-white border-neutral-300 text-neutral-500 hover:bg-neutral-50"} relative inline-flex items-center px-4 py-1 border text-sm font-medium`}>{i + 1}</button>
                  );
                }
                if (currentPage < totalPages - 3) buttons.push(<span key="e2" className="relative inline-flex items-center px-4 py-1 border border-neutral-300 bg-white text-sm font-medium text-neutral-550">...</span>);
                if (currentPage < totalPages - 2 && totalPages > 1) {
                  buttons.push(
                    <button key={totalPages - 1} onClick={() => { setCurrentPage(totalPages - 1); resetScroll(); }}
                      className="bg-white border-neutral-300 text-neutral-500 hover:bg-neutral-50 relative inline-flex items-center px-4 py-1 border text-sm font-medium">{totalPages}</button>
                  );
                }

                buttons.push(
                  <button key="next"
                    onClick={() => {
                      if (currentPage === totalPages - 1 && hasNext && onNext) { onNext(); setCurrentPage(0); resetScroll(); }
                      else { setCurrentPage(Math.min(totalPages - 1, currentPage + 1)); resetScroll(); }
                    }}
                    disabled={(currentPage === totalPages - 1 && !hasNext) || totalPages === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-neutral-300 bg-white text-sm font-medium text-neutral-500 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed">
                    Suivant
                  </button>
                );
                return buttons;
              })()}
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataGrid;
